/**
 * SuperAdminDashboard - Dashboard pentru proprietarul platformei e-Tesa
 * Gestionează firme, vizualizează angajați, monitorizează plăți
 */
import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  LogOut, Building2, Users, CreditCard, BarChart3, Plus, Trash2, 
  Eye, Mail, Phone, Calendar, ChevronRight, Crown, Settings, Headphones,
  Search, Loader2, MapPin, FileText, Landmark, Hash, UserCog, Info,
  ChevronDown, ChevronUp, Home, Clock, Wrench, Car, FileCheck, Bell,
  MessageSquare, HelpCircle, Shield, Lock, User, Download, Upload, ArrowRight, Code, Database,
  RefreshCw, Save, MailCheck, ShieldAlert
} from "lucide-react";
import SupportTab from "@/components/dashboard/SupportTab";
import CodeViewer from "@/components/dashboard/CodeViewer";
import DevizKnowledgeTab from "@/components/dashboard/DevizKnowledgeTab";
import { formatDateRO, formatDateTimeRO } from "@/utils/dateFormat";
import GlassLayout from "@/components/GlassLayout";
import InvoicePreviewDialog from "@/components/dashboard/InvoicePreviewDialog";
import EmailToolsCard from "@/pages/EmailToolsCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get user from either storage
function getStoredUser() {
  const fromLocal = localStorage.getItem('user');
  const fromSession = sessionStorage.getItem('user');
  const raw = fromLocal || fromSession;
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    console.error('Failed to parse stored user:', error);
    return null;
  }
}

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const user = getStoredUser() || {};
  
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({});
  const [payments, setPayments] = useState({ payments: [], message: "" });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyEmployees, setCompanyEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create company dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", new: "", confirm: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [newCompany, setNewCompany] = useState({ 
    company_name: "", 
    email: "", 
    phone: "", 
    password: "",
    cui: "",
    address: "",
    reg_com: "",
    bank_account: "",
    bank_name: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [lookingUpCUI, setLookingUpCUI] = useState(false);
  
  // Credits / Payments state
  const [companyCredits, setCompanyCredits] = useState([]);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsSortBy, setCreditsSortBy] = useState("consumed_desc");
  const [showAllocateDialog, setShowAllocateDialog] = useState(false);
  const [allocateTarget, setAllocateTarget] = useState(null);
  const [allocateAmount, setAllocateAmount] = useState("");
  const [allocateDesc, setAllocateDesc] = useState("Atribuire credite de către SuperAdmin");
  const [allocating, setAllocating] = useState(false);
  const [showTransactionsDialog, setShowTransactionsDialog] = useState(false);
  const [transactionsTarget, setTransactionsTarget] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  
  // Action Prices state
  const [actionPrices, setActionPrices] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [editingPrices, setEditingPrices] = useState({});
  const [savingPrice, setSavingPrice] = useState(null);
  
  // Backup & Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importMode, setImportMode] = useState("fresh");
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState({});
  const [showBackupsDialog, setShowBackupsDialog] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backupRunning, setBackupRunning] = useState(false);
  const companyUploadRef = useRef(null);
  const [uploadTargetCompany, setUploadTargetCompany] = useState(null);
  
  // Manage company mode - pentru a gestiona o firmă ca administrator
  const [managingCompany, setManagingCompany] = useState(null);

  // Controlled tab for back button handling
  const [activeTab, setActiveTab] = useState("companies");

  // Pending credit requests count (for badge on Plăți tab)
  const [pendingCreditRequests, setPendingCreditRequests] = useState(0);
  useEffect(() => {
    const fetchPending = async () => {
      try {
        const r = await axios.get(`${API}/superadmin/credit-requests/count-pending`);
        setPendingCreditRequests(r.data?.pending || 0);
      } catch { /* ignore */ }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, []);

  // Set document title for SuperAdmin
  useEffect(() => {
    document.title = "e-Tesa MASTER - Super Administrator";
    return () => {
      document.title = "e-Tesa";
    };
  }, []);

  // Handle browser back button - navigate to first tab instead of logout
  useEffect(() => {
    const handleBackButton = () => {
      if (activeTab !== "companies") {
        setActiveTab("companies");
        window.history.pushState({ tab: "companies" }, "");
      } else {
        window.history.pushState({ tab: "companies" }, "");
      }
    };
    window.history.pushState({ tab: activeTab }, "");
    window.addEventListener("popstate", handleBackButton);
    return () => window.removeEventListener("popstate", handleBackButton);
  }, [activeTab]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    window.history.pushState({ tab: newTab }, "");
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [companiesRes, statsRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/superadmin/companies`),
        axios.get(`${API}/superadmin/stats`),
        axios.get(`${API}/superadmin/payments`)
      ]);
      setCompanies(companiesRes.data);
      setStats(statsRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user.role !== 'superadmin') {
      navigate('/');
      return;
    }
    fetchAll();
  }, [user.role, navigate, fetchAll]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    navigate('/');
  };

  // Credits functions
  const fetchCompanyCredits = useCallback(async () => {
    setCreditsLoading(true);
    try {
      const res = await axios.get(`${API}/credits/all-companies`);
      setCompanyCredits(res.data);
    } catch (e) {
      console.error("Error fetching credits:", e);
    } finally {
      setCreditsLoading(false);
    }
  }, []);

  const fetchActionPrices = useCallback(async () => {
    setPricesLoading(true);
    try {
      const res = await axios.get(`${API}/credits/prices`);
      setActionPrices(res.data);
      const priceMap = {};
      res.data.forEach(p => { priceMap[p.action_code] = p.price; });
      setEditingPrices(priceMap);
    } catch (e) {
      console.error("Error fetching prices:", e);
    } finally {
      setPricesLoading(false);
    }
  }, []);

  const handleSavePrice = async (actionCode) => {
    const newPrice = parseFloat(editingPrices[actionCode]);
    if (isNaN(newPrice) || newPrice < 0) {
      toast.error("Prețul trebuie să fie 0 sau mai mare");
      return;
    }
    setSavingPrice(actionCode);
    try {
      await axios.put(`${API}/credits/prices`, {
        action_code: actionCode,
        price: newPrice
      });
      toast.success("Preț actualizat!");
      fetchActionPrices();
    } catch (e) {
      toast.error("Eroare la salvare");
    } finally {
      setSavingPrice(null);
    }
  };

  const getSortedCredits = () => {
    const sorted = [...companyCredits];
    switch (creditsSortBy) {
      case "consumed_desc":
        return sorted.sort((a, b) => (b.consumed || 0) - (a.consumed || 0));
      case "consumed_asc":
        return sorted.sort((a, b) => (a.consumed || 0) - (b.consumed || 0));
      case "name_asc":
        return sorted.sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""));
      case "name_desc":
        return sorted.sort((a, b) => (b.company_name || "").localeCompare(a.company_name || ""));
      case "date_desc":
        return sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      case "date_asc":
        return sorted.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      case "balance_desc":
        return sorted.sort((a, b) => (b.balance || 0) - (a.balance || 0));
      case "balance_asc":
        return sorted.sort((a, b) => (a.balance || 0) - (b.balance || 0));
      default:
        return sorted;
    }
  };

  const handleAllocateCredits = async () => {
    if (!allocateTarget || !allocateAmount || parseFloat(allocateAmount) <= 0) {
      toast.error("Introduceți o sumă validă");
      return;
    }
    setAllocating(true);
    try {
      const res = await axios.post(
        `${API}/credits/admin-add?company_id=${allocateTarget.company_id}&amount=${parseFloat(allocateAmount)}&description=${encodeURIComponent(allocateDesc)}`
      );
      toast.success(`${res.data.message} - Sold nou: ${res.data.new_balance} credite`);
      setShowAllocateDialog(false);
      setAllocateAmount("");
      fetchCompanyCredits();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare la atribuire credite");
    } finally {
      setAllocating(false);
    }
  };

  const fetchTransactions = async (companyId) => {
    setTransactionsLoading(true);
    try {
      const res = await axios.get(`${API}/credits/transactions?company_id=${companyId}&limit=100`);
      setTransactions(res.data.transactions || []);
    } catch (e) {
      console.error("Error fetching transactions:", e);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.new || !passwordForm.confirm) {
      toast.error("Completează toate câmpurile");
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error("Parolele noi nu coincid");
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error("Parola nouă trebuie să aibă minim 6 caractere");
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        user_id: user.id,
        current_password: passwordForm.current,
        new_password: passwordForm.new
      });
      toast.success("Parola a fost schimbată cu succes!");
      setShowChangePassword(false);
      setPasswordForm({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la schimbarea parolei");
    } finally {
      setPasswordLoading(false);
    }
  };



  const handleViewEmployees = async (company) => {
    setSelectedCompany(company);
    try {
      const response = await axios.get(`${API}/superadmin/companies/${company.company_id}/employees`);
      setCompanyEmployees(response.data);
    } catch (error) {
      toast.error("Eroare la încărcarea angajaților");
    }
  };

  const handleLookupCUI = async () => {
    if (!newCompany.cui || newCompany.cui.length < 5) {
      toast.error("Introduceți un CUI valid (minim 5 cifre)");
      return;
    }
    
    setLookingUpCUI(true);
    try {
      const response = await axios.post(`${API}/superadmin/lookup-cui`, { cui: newCompany.cui });
      
      if (response.data.found) {
        setNewCompany({
          ...newCompany,
          company_name: response.data.company_name || newCompany.company_name,
          address: response.data.address || newCompany.address,
          reg_com: response.data.reg_com || newCompany.reg_com,
          cui: response.data.cui || newCompany.cui,
          phone: response.data.phone || newCompany.phone
        });
        toast.success(`Date găsite de pe ${response.data.source}!`);
      } else {
        toast.error(response.data.message || "Nu am găsit date pentru acest CUI");
      }
    } catch (error) {
      toast.error("Eroare la căutarea datelor firmei");
    } finally {
      setLookingUpCUI(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/superadmin/companies`, newCompany);
      toast.success("Firmă creată cu succes!");
      setShowCreateDialog(false);
      setNewCompany({ 
        company_name: "", email: "", phone: "", password: "",
        cui: "", address: "", reg_com: "", bank_account: "", bank_name: ""
      });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la creare");
    }
  };

  const handleManageCompany = (company) => {
    // Read superadmin user from whichever storage has it (Login uses sessionStorage
    // when "Ține-mă logat" is unchecked, otherwise localStorage).
    const rawLocal = localStorage.getItem('user');
    const rawSession = sessionStorage.getItem('user');
    const originalStorage = rawLocal ? 'local' : (rawSession ? 'session' : 'local');
    const originalRaw = rawLocal || rawSession || '{}';
    let originalUser = {};
    try { originalUser = JSON.parse(originalRaw); } catch { originalUser = {}; }

    // Salvăm datele firmei pentru a le folosi în MasterDashboard
    const managingData = {
      company_id: company.company_id,
      company_name: company.company_name,
      email: company.email,
      isSuperadminManaging: true,
      original_user: originalUser,
      original_storage: originalStorage
    };

    // Creăm un user temporar cu datele firmei pentru MasterDashboard
    const tempUser = {
      ...originalUser,
      company_id: company.company_id,
      company_name: company.company_name,
      role: 'admin', // Temporar admin pentru a accesa funcționalitățile
      isSuperadminManaging: true
    };

    localStorage.setItem('superadmin_managing', JSON.stringify(managingData));
    // Write tempUser back to the SAME storage the superadmin was using so the
    // ProtectedRoute guards continue to see a valid session.
    if (originalStorage === 'session') {
      sessionStorage.setItem('user', JSON.stringify(tempUser));
      localStorage.removeItem('user');
    } else {
      localStorage.setItem('user', JSON.stringify(tempUser));
      sessionStorage.removeItem('user');
    }
    localStorage.setItem('company_id', company.company_id);
    localStorage.setItem('company_name', company.company_name);

    // Navigăm către MasterDashboard
    navigate('/master');
  };

  const handleDeleteCompany = async (companyId) => {
    if (!companyId) {
      toast.error("ID-ul companiei lipsește");
      return;
    }
    if (!confirm("Sigur doriți să ștergeți această firmă? Toate datele vor fi pierdute.")) return;
    try {
      await axios.delete(`${API}/superadmin/companies/${companyId}`);
      toast.success("Firma a fost ștearsă");
      fetchAll();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleToggleEmailVerified = async (company) => {
    if (!company?.id) {
      toast.error("ID utilizator lipsă");
      return;
    }
    const currentlyVerified = !!company.email_verified;
    const action = currentlyVerified ? "anulezi validarea" : "validezi";
    if (!confirm(`Sigur dorești să ${action} email-ul firmei "${company.company_name}" (${company.email})?`)) return;
    try {
      await axios.post(`${API}/superadmin/companies/${company.id}/verify-email`, {
        verified: !currentlyVerified
      });
      toast.success(currentlyVerified ? "Validare anulată" : "Email validat manual");
      fetchAll();
    } catch (error) {
      toast.error("Eroare: " + (error.response?.data?.detail || error.message));
    }
  };

  // ============== BACKUP & RESTORE FUNCTIONS ==============
  
  const handleExportCompany = async (company) => {
    const companyId = company.company_id || company.id;
    setExportLoading(prev => ({ ...prev, [companyId]: true }));
    try {
      const response = await axios.get(`${API}/superadmin/company/${companyId}/export`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = (company.company_name || 'firma').replace(/[^a-zA-Z0-9]/g, '_');
      const date = new Date().toISOString().split('T')[0];
      link.href = url;
      link.download = `backup_${safeName}_${date}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup descărcat: ${company.company_name} (${response.data.total_records} înregistrări)`);
    } catch (error) {
      toast.error("Eroare la export: " + (error.response?.data?.detail || error.message));
    } finally {
      setExportLoading(prev => ({ ...prev, [companyId]: false }));
    }
  };

  const handleExportAll = async () => {
    setExportLoading(prev => ({ ...prev, all: true }));
    try {
      const response = await axios.get(`${API}/superadmin/export-all`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const date = new Date().toISOString().split('T')[0];
      link.href = url;
      link.download = `backup_TOATE_FIRMELE_${date}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup complet: ${response.data.total_companies} firme, ${response.data.total_records} înregistrări`);
    } catch (error) {
      toast.error("Eroare la export");
    } finally {
      setExportLoading(prev => ({ ...prev, all: false }));
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        // Check if it's a single company or full backup
        if (data.companies) {
          // Full backup - let user choose which company
          setImportData({ type: 'multi', data, companies: data.companies });
        } else if (data.collections) {
          // Single company backup
          setImportData({ type: 'single', data });
        } else {
          toast.error("Format de backup invalid");
          return;
        }
        setShowImportDialog(true);
      } catch (err) {
        toast.error("Fișierul nu este valid JSON");
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Upload (import) for a specific company
  const handleCompanyUploadClick = (company) => {
    setUploadTargetCompany(company);
    if (companyUploadRef.current) {
      companyUploadRef.current.click();
    }
  };

  const handleCompanyUploadFile = (e) => {
    const file = e.target.files[0];
    if (!file || !uploadTargetCompany) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = JSON.parse(event.target.result);
        // Accept single company backup or multi
        let companyData;
        if (data.collections) {
          companyData = data;
        } else if (data.companies) {
          // Multi-backup — find the matching company or take the first
          const targetId = uploadTargetCompany.company_id || uploadTargetCompany.id;
          const match = data.companies.find(c =>
            c.company_id === targetId || c.id === targetId || c.company_name === uploadTargetCompany.name
          );
          companyData = match || data.companies[0];
        } else {
          toast.error("Format de backup invalid");
          return;
        }

        setImportMode("upgrade");
        setImportLoading(true);
        try {
          const response = await axios.post(
            `${API}/superadmin/company/import-json?mode=upgrade`,
            companyData
          );
          toast.success(`${uploadTargetCompany.name}: ${response.data.message}`);
          fetchAll();
        } catch (error) {
          toast.error(`Eroare import ${uploadTargetCompany.name}: ${error.response?.data?.detail || error.message}`);
        } finally {
          setImportLoading(false);
          setUploadTargetCompany(null);
        }
      } catch (err) {
        toast.error("Fișierul nu este valid JSON");
        setUploadTargetCompany(null);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImportCompany = async (companyData) => {
    setImportLoading(true);
    try {
      const response = await axios.post(
        `${API}/superadmin/company/import-json?mode=${importMode}`,
        companyData
      );
      toast.success(response.data.message);
      setShowImportDialog(false);
      setImportData(null);
      fetchAll();
    } catch (error) {
      toast.error("Eroare la import: " + (error.response?.data?.detail || error.message));
    } finally {
      setImportLoading(false);
    }
  };

  const fetchBackups = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/backups`);
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error("Error fetching backups:", error);
    }
  };

  const handleRunBackupNow = async () => {
    setBackupRunning(true);
    try {
      const response = await axios.post(`${API}/superadmin/backups/run-now`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchBackups();
      } else {
        toast.error("Backup eșuat: " + response.data.error);
      }
    } catch (error) {
      toast.error("Eroare la backup");
    } finally {
      setBackupRunning(false);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const response = await axios.get(`${API}/superadmin/backups/${filename}/download`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup descărcat: ${filename}`);
    } catch (error) {
      toast.error("Eroare la descărcare");
    }
  };

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewCompany({ ...newCompany, password });
  };

  const generatePlatformPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;
    const lineHeight = 6;
    const indent1 = 15;
    const indent2 = 25;
    const indent3 = 35;
    
    // Title
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("e-Tesa - Ghid Complet Platforma", pageWidth / 2, y, { align: "center" });
    y += 10;
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Generat: ${formatDateRO(new Date())}`, pageWidth / 2, y, { align: "center" });
    y += 15;
    
    // Table of Contents Header
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("CUPRINS DETALIAT", indent1, y);
    y += 10;
    
    // Section 1: Worker Dashboard
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("1. DASHBOARD ANGAJAT (WORKER)", indent1, y);
    y += lineHeight;
    
    const workerItems = [
      { name: "1.1 PREZENT / SOSIT", desc: "Check-in la inceputul programului", link: "Genereaza notificare Admin + Calendar Pontaj" },
      { name: "1.2 Selectare Santier", desc: "Alege din lista de santiere", link: "Date din tab-ul Santiere (Admin)" },
      { name: "1.3 Completeaza Punct de Lucru", desc: "Interventii in alte locatii", link: "Pontaj fara santier predefinit" },
      { name: "1.4 Selectare Unelte", desc: "Marcheaza uneltele utilizate", link: "Date din tab-ul Unelte + Ore utilizare" },
      { name: "1.5 Masina de Firma", desc: "Monitorizare traseu auto", link: "Genereaza Foaie Parcurs + Documente" },
      { name: "1.6 Solicit Concediu", desc: "Cerere CO/CM/CS/FS", link: "Cereri in Asteptare + Notificari + Documente" },
      { name: "1.7 INCHEIERE PROGRAM", desc: "Check-out la finalul zilei", link: "Calcul ore lucrate + Notificare Admin" },
    ];
    
    doc.setFontSize(9);
    workerItems.forEach(item => {
      doc.setFont("helvetica", "bold");
      doc.text(item.name, indent2, y);
      y += lineHeight - 1;
      doc.setFont("helvetica", "normal");
      doc.text(`   ${item.desc}`, indent2, y);
      y += lineHeight - 1;
      doc.setFont("helvetica", "italic");
      doc.text(`   -> ${item.link}`, indent3, y);
      y += lineHeight;
    });
    
    y += 5;
    
    // Section 2: Admin Dashboard
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("2. DASHBOARD ADMINISTRATOR FIRMA", indent1, y);
    y += lineHeight;
    
    const adminItems = [
      { name: "2.1 Acasa", desc: "Sumar prezente si statistici zilnice", link: "Date agregate din Pontaj + Unelte" },
      { name: "2.2 Notificari", desc: "Cereri, alerte, mesaje sistem", link: "Primite de la: Angajati + Sistem" },
      { name: "2.3 Santiere", desc: "Creare/editare locatii cu GPS", link: "Disponibile in: Worker + Sarcini + Rapoarte" },
      { name: "2.4 Unelte", desc: "Inventar echipamente firma", link: "Disponibile in: Worker + Ore utilizare Acasa" },
      { name: "2.5 Angajati", desc: "Creare conturi, asignare proiecte", link: "Acces Worker Dashboard + Calendar Pontaj" },
      { name: "2.6 Pontaj", desc: "Calendar prezente + concedii", link: "Date de la: Check-in Worker + Export PDF/Excel" },
      { name: "2.7 Foi de Parcurs", desc: "Trasee vehicule firma", link: "Generate automat de Worker + Documente" },
      { name: "2.8 Sarcini", desc: "Tichete interventii cu prioritate", link: "Asignate la Angajati + Status in Worker" },
      { name: "2.9 Documente", desc: "Registru automat numerotare", link: "Populate de: Cereri concediu + Foi parcurs" },
      { name: "2.10 Deviz", desc: "Generator devize estimative", link: "Salvare draft + Export PDF + Auto-numerotare" },
    ];
    
    doc.setFontSize(9);
    adminItems.forEach(item => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.setFont("helvetica", "bold");
      doc.text(item.name, indent2, y);
      y += lineHeight - 1;
      doc.setFont("helvetica", "normal");
      doc.text(`   ${item.desc}`, indent2, y);
      y += lineHeight - 1;
      doc.setFont("helvetica", "italic");
      doc.text(`   -> ${item.link}`, indent3, y);
      y += lineHeight;
    });
    
    y += 5;
    if (y > 240) {
      doc.addPage();
      y = 20;
    }
    
    // Section 3: SuperAdmin Dashboard
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("3. DASHBOARD SUPER ADMINISTRATOR", indent1, y);
    y += lineHeight;
    
    const superadminItems = [
      { name: "3.1 Firme", desc: "Creare, gestionare, stergere conturi", link: "Fiecare firma = Admin Dashboard izolat" },
      { name: "3.2 Plati", desc: "Monitorizare abonamente", link: "Status abonament per firma" },
      { name: "3.3 Suport", desc: "Erori raportate de toate firmele", link: "Primite de la AI Chat (RAPORTEAZA EROARE)" },
      { name: "3.4 Gestioneaza Firma", desc: "Acces admin la orice firma", link: "Deschide Admin Dashboard cu datele firmei" },
      { name: "3.5 Vizualizare Angajati", desc: "Lista angajati per firma", link: "Vedere rapida fara Admin Dashboard" },
    ];
    
    doc.setFontSize(9);
    superadminItems.forEach(item => {
      doc.setFont("helvetica", "bold");
      doc.text(item.name, indent2, y);
      y += lineHeight - 1;
      doc.setFont("helvetica", "normal");
      doc.text(`   ${item.desc}`, indent2, y);
      y += lineHeight - 1;
      doc.setFont("helvetica", "italic");
      doc.text(`   -> ${item.link}`, indent3, y);
      y += lineHeight;
    });
    
    y += 5;
    
    // Section 4: AI Chat
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("4. AI CHAT ASSISTANT", indent1, y);
    y += lineHeight;
    
    const chatItems = [
      { name: "4.1 Intrebari Constructii", desc: "Preturi materiale, sfaturi tehnice", link: "Raspunsuri AI instant" },
      { name: "4.2 RAPORTEAZA EROARE", desc: "Trimite bug-uri catre suport", link: "Creaza tichet in tab Suport SuperAdmin" },
      { name: "4.3 STATUS ERORI", desc: "Vezi erorile raportate", link: "Admin = erori firma, SuperAdmin = toate" },
    ];
    
    doc.setFontSize(9);
    chatItems.forEach(item => {
      doc.setFont("helvetica", "bold");
      doc.text(item.name, indent2, y);
      y += lineHeight - 1;
      doc.setFont("helvetica", "normal");
      doc.text(`   ${item.desc}`, indent2, y);
      y += lineHeight - 1;
      doc.setFont("helvetica", "italic");
      doc.text(`   -> ${item.link}`, indent3, y);
      y += lineHeight;
    });
    
    y += 10;
    
    // Footer
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("e-Tesa - Time & Work Management | Versiune 1.0.0", pageWidth / 2, 285, { align: "center" });
    
    // Save PDF
    doc.save("e-Tesa_Ghid_Platforma.pdf");
    toast.success("PDF generat cu succes!");
  };

  return (
    <GlassLayout variant="superadmin">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                e-Tesa <span className="text-orange-500">MASTER</span>
              </h1>
              <p className="text-xs text-slate-500">Platform Administration</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user.email}</span>
            <Button 
              variant="ghost" 
              onClick={() => setShowSettingsDialog(true)}
              className="text-slate-700 hover:bg-white/30"
            >
              <Settings className="w-4 h-4 mr-2" />
              Setări
            </Button>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-slate-700 hover:bg-white/30"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Ieșire
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white/45 backdrop-blur-xl border-white/60">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Firme</p>
                  <p className="text-3xl font-bold text-slate-800">{stats.total_companies || 0}</p>
                </div>
                <Building2 className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/45 backdrop-blur-xl border-white/60">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Angajați</p>
                  <p className="text-3xl font-bold text-slate-800">{stats.total_workers || 0}</p>
                </div>
                <Users className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/45 backdrop-blur-xl border-white/60">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Șantiere</p>
                  <p className="text-3xl font-bold text-slate-800">{stats.total_sites || 0}</p>
                </div>
                <BarChart3 className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/45 backdrop-blur-xl border-white/60">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Abonamente Active</p>
                  <p className="text-3xl font-bold text-slate-800">{stats.active_subscriptions || 0}</p>
                </div>
                <CreditCard className="w-10 h-10 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="bg-white/40 backdrop-blur-xl border-white/50 flex overflow-x-auto scrollbar-hide w-full" style={{WebkitOverflowScrolling: 'touch'}}>
            <TabsTrigger value="companies" className="flex-shrink-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" />
              Firme
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex-shrink-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white relative">
              <CreditCard className="w-4 h-4 mr-2" />
              Plăți
              {pendingCreditRequests > 0 && (
                <span 
                  className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white animate-pulse" 
                  data-testid="superadmin-pending-credits-badge"
                  title={`${pendingCreditRequests} cereri de credite în așteptare`}
                >
                  {pendingCreditRequests}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="support" className="flex-shrink-0 data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="support-tab">
              <Headphones className="w-4 h-4 mr-2" />
              Suport
            </TabsTrigger>
            <TabsTrigger value="code" className="flex-shrink-0 data-[state=active]:bg-teal-500 data-[state=active]:text-white" data-testid="code-tab">
              <Code className="w-4 h-4 mr-2" />
              Cod Sursă
            </TabsTrigger>
            <TabsTrigger value="knowledge" className="flex-shrink-0 data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-purple-500 data-[state=active]:text-white" data-testid="knowledge-tab">
              <Database className="w-4 h-4 mr-2" />
              Devize Învățate
            </TabsTrigger>
          </TabsList>

          {/* Companies Tab */}
          <TabsContent value="companies">
            <EmailToolsCard />
            <Card className="bg-white/45 backdrop-blur-xl border-white/60">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-slate-800">Firme Înregistrate</CardTitle>
                  <CardDescription className="text-slate-500">Gestionează conturile firmelor</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setShowCreateDialog(true)}
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adaugă Firmă
                  </Button>
                  <label>
                    <input type="file" accept=".json" onChange={handleImportFile} className="hidden" />
                    {/* Hidden file input for per-company upload */}
                    <input ref={companyUploadRef} type="file" accept=".json" onChange={handleCompanyUploadFile} className="hidden" />
                    <Button
                      variant="outline"
                      className="border-green-400 text-green-400 hover:bg-green-500/10 cursor-pointer"
                      asChild
                    >
                      <span><Download className="w-4 h-4 mr-2 rotate-180" />Importă Firmă</span>
                    </Button>
                  </label>
                  <Button
                    variant="outline"
                    onClick={handleExportAll}
                    disabled={exportLoading.all}
                    className="border-blue-400 text-blue-400 hover:bg-blue-500/10"
                  >
                    {exportLoading.all ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Export Toate
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { fetchBackups(); setShowBackupsDialog(true); }}
                    className="border-blue-400 text-blue-400 hover:bg-blue-500/10"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Backup-uri
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  {loading ? (
                    <p className="text-center text-slate-400 py-8">Se încarcă...</p>
                  ) : companies.length === 0 ? (
                    <p className="text-center text-slate-400 py-8">Nicio firmă înregistrată</p>
                  ) : (
                    <div className="space-y-3">
                      {companies.map((company) => (
                        <div 
                          key={company.id}
                          className="p-4 bg-white/30 rounded-xl border border-white/50 hover:bg-white/50 transition-colors"
                        >
                          <div className="flex flex-col gap-3">
                            {/* Company Info */}
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Building2 className="w-5 h-5 text-white" />
                              </div>
                              <div className="min-w-0">
                                <h3 className="font-semibold text-slate-800 truncate">{company.company_name}</h3>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{company.email}</span>
                                  </span>
                                  {company.phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-3 h-3 flex-shrink-0" />
                                      {company.phone}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* Badges */}
                            <div className="flex flex-wrap gap-2">
                              <Badge variant="outline" className="border-blue-400 text-blue-400">
                                <Users className="w-3 h-3 mr-1" />
                                {company.workers_count} angajați
                              </Badge>
                              <Badge variant="outline" className="border-green-400 text-green-400">
                                {company.payment_status === 'active' ? '✓ Activ' : 'Inactiv'}
                              </Badge>
                              <Badge variant="outline" className="border-yellow-400 text-yellow-400">
                                {company.subscription_type}
                              </Badge>
                              {company.email_verified ? (
                                <Badge variant="outline" className="border-emerald-500 text-emerald-600" data-testid={`email-verified-badge-${company.id}`}>
                                  <MailCheck className="w-3 h-3 mr-1" />
                                  Email validat
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="border-orange-500 text-orange-600" data-testid={`email-unverified-badge-${company.id}`}>
                                  <ShieldAlert className="w-3 h-3 mr-1" />
                                  Email nevalidat
                                </Badge>
                              )}
                            </div>
                            {/* Actions - stacked on mobile */}
                            <div className="flex flex-wrap gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleExportCompany(company)}
                                disabled={exportLoading[company.company_id || company.id]}
                                className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                title="Descarcă backup firmă"
                              >
                                {exportLoading[company.company_id || company.id] ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4 mr-1" />
                                )}
                                Export
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCompanyUploadClick(company)}
                                disabled={importLoading && uploadTargetCompany?.id === company.id}
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                                title="Încarcă date firmă din backup JSON"
                                data-testid={`upload-company-${company.company_id || company.id}`}
                              >
                                {importLoading && (uploadTargetCompany?.id === company.id || uploadTargetCompany?.company_id === (company.company_id || company.id)) ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Upload className="w-4 h-4 mr-1" />
                                )}
                                Import
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleManageCompany(company)}
                                className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"
                              >
                                <Settings className="w-4 h-4 mr-1" />
                                Gestionează
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewEmployees(company)}
                                className="text-slate-400 hover:text-slate-700"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Angajați
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleEmailVerified(company)}
                                className={company.email_verified
                                  ? "text-orange-500 hover:text-orange-600 hover:bg-orange-500/10"
                                  : "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10"}
                                title={company.email_verified ? "Anulează validarea email" : "Validează email manual"}
                                data-testid={`toggle-email-verify-${company.id}`}
                              >
                                {company.email_verified ? (
                                  <ShieldAlert className="w-4 h-4 mr-1" />
                                ) : (
                                  <MailCheck className="w-4 h-4 mr-1" />
                                )}
                                {company.email_verified ? "Anulează validare" : "Validează email"}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCompany(company.id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Șterge
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <PaymentsTab
              companyCredits={companyCredits}
              creditsLoading={creditsLoading}
              creditsSortBy={creditsSortBy}
              setCreditsSortBy={setCreditsSortBy}
              fetchCompanyCredits={fetchCompanyCredits}
              getSortedCredits={getSortedCredits}
              actionPrices={actionPrices}
              pricesLoading={pricesLoading}
              editingPrices={editingPrices}
              setEditingPrices={setEditingPrices}
              fetchActionPrices={fetchActionPrices}
              handleSavePrice={handleSavePrice}
              savingPrice={savingPrice}
              onAllocate={(company) => {
                setAllocateTarget(company);
                setAllocateAmount("");
                setAllocateDesc("Atribuire credite de către SuperAdmin");
                setShowAllocateDialog(true);
              }}
              onViewTransactions={(company) => {
                setTransactionsTarget(company);
                fetchTransactions(company.company_id);
                setShowTransactionsDialog(true);
              }}
            />
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support">
            <SupportTab />
          </TabsContent>

          {/* Code Viewer Tab */}
          <TabsContent value="code">
            <Card className="bg-white/45 backdrop-blur-xl border-white/60">
              <CardContent className="pt-6">
                <CodeViewer />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="knowledge">
            <DevizKnowledgeTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Company Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-white/70 backdrop-blur-xl border-white/50 text-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-yellow-500" />
              Creare Cont Firmă
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Introduceți CUI-ul pentru completare automată sau completați manual datele
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCompany} className="space-y-4">
            {/* CUI Lookup Section */}
            <div className="bg-white/20 p-4 rounded-lg border border-slate-200">
              <Label className="text-yellow-400 flex items-center gap-2 mb-2">
                <Hash className="w-4 h-4" />
                Căutare automată după CUI
              </Label>
              <div className="flex gap-2">
                <Input
                  value={newCompany.cui}
                  onChange={(e) => setNewCompany({ ...newCompany, cui: e.target.value })}
                  placeholder="Ex: 12345678 sau RO12345678"
                  className="bg-white/40 border-slate-200 flex-1"
                />
                <Button 
                  type="button" 
                  onClick={handleLookupCUI}
                  disabled={lookingUpCUI}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  {lookingUpCUI ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  <span className="ml-2">{lookingUpCUI ? "Caut..." : "Caută"}</span>
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Datele se completează automat de pe ANAF / ListaFirme.ro
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Nume Firmă */}
              <div className="col-span-2">
                <Label className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  Nume Firmă *
                </Label>
                <Input
                  value={newCompany.company_name}
                  onChange={(e) => setNewCompany({ ...newCompany, company_name: e.target.value })}
                  placeholder="SC EXEMPLU SRL"
                  required
                  className="bg-white/40 border-slate-200"
                />
              </div>

              {/* Nr. Reg. Comerț */}
              <div>
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-400" />
                  Nr. Reg. Comerț
                </Label>
                <Input
                  value={newCompany.reg_com}
                  onChange={(e) => setNewCompany({ ...newCompany, reg_com: e.target.value })}
                  placeholder="J40/1234/2020"
                  className="bg-white/40 border-slate-200"
                />
              </div>

              {/* Telefon */}
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  Telefon
                </Label>
                <Input
                  value={newCompany.phone}
                  onChange={(e) => setNewCompany({ ...newCompany, phone: e.target.value })}
                  placeholder="0722 123 456"
                  className="bg-white/40 border-slate-200"
                />
              </div>

              {/* Adresă */}
              <div className="col-span-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  Adresă Sediu
                </Label>
                <Input
                  value={newCompany.address}
                  onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })}
                  placeholder="Str. Exemplu nr. 10, București"
                  className="bg-white/40 border-slate-200"
                />
              </div>

              {/* Bancă */}
              <div>
                <Label className="flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-slate-400" />
                  Bancă
                </Label>
                <Input
                  value={newCompany.bank_name}
                  onChange={(e) => setNewCompany({ ...newCompany, bank_name: e.target.value })}
                  placeholder="BCR / BRD / ING"
                  className="bg-white/40 border-slate-200"
                />
              </div>

              {/* Cont Bancar */}
              <div>
                <Label className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-slate-400" />
                  IBAN
                </Label>
                <Input
                  value={newCompany.bank_account}
                  onChange={(e) => setNewCompany({ ...newCompany, bank_account: e.target.value })}
                  placeholder="RO49AAAA..."
                  className="bg-white/40 border-slate-200"
                />
              </div>
            </div>

            {/* Secțiune Acces */}
            <div className="border-t border-slate-200 pt-4 mt-4">
              <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <UserCog className="w-4 h-4" />
                Date de Acces Administrator
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    Email Login *
                  </Label>
                  <Input
                    type="email"
                    value={newCompany.email}
                    onChange={(e) => setNewCompany({ ...newCompany, email: e.target.value })}
                    placeholder="admin@firma.ro"
                    required
                    className="bg-white/40 border-slate-200"
                  />
                </div>
                <div>
                  <Label>Parolă *</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={newCompany.password}
                        onChange={(e) => setNewCompany({ ...newCompany, password: e.target.value })}
                        placeholder="Parolă"
                        required
                        className="bg-white/40 border-slate-200 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                    <Button type="button" variant="outline" onClick={generatePassword} className="border-slate-200">
                      Generează
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="ghost" onClick={() => setShowCreateDialog(false)} className="flex-1">
                Anulează
              </Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500">
                <Plus className="w-4 h-4 mr-2" />
                Creează Cont
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Employees Dialog */}
      <Dialog open={!!selectedCompany} onOpenChange={() => setSelectedCompany(null)}>
        <DialogContent className="bg-white/70 backdrop-blur-xl border-white/50 text-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle>Angajați - {selectedCompany?.company_name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Lista angajaților firmei
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            {companyEmployees.length === 0 ? (
              <p className="text-center text-slate-400 py-8">Niciun angajat înregistrat</p>
            ) : (
              <div className="space-y-2">
                {companyEmployees.map((emp) => (
                  <div key={emp.id} className="p-3 bg-white/30 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-semibold">{emp.name}</p>
                      <p className="text-sm text-slate-400">{emp.email || "(fără email)"}</p>
                    </div>
                    <Badge variant="outline" className="border-slate-500">
                      {emp.role}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="bg-white/70 backdrop-blur-xl border-white/50 text-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-yellow-500" />
              Setări Cont Administrator
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Gestionează datele contului de administrare platformă
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Account Info */}
            <div className="p-4 bg-white/20 rounded-lg border border-slate-200">
              <h4 className="font-semibold text-yellow-400 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />
                Informații Cont
              </h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-slate-400 text-xs">Nume</Label>
                  <Input 
                    value={user.name || "Super Administrator"} 
                    className="bg-white/40 border-slate-200"
                    disabled
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Email</Label>
                  <Input 
                    value={user.email || ""} 
                    className="bg-white/40 border-slate-200"
                    disabled
                  />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Rol</Label>
                  <Input 
                    value="Super Administrator (Platformă)" 
                    className="bg-white/40 border-slate-200"
                    disabled
                  />
                </div>
              </div>
            </div>

            {/* Security */}
            <div className="p-4 bg-white/20 rounded-lg border border-slate-200">
              <h4 className="font-semibold text-yellow-400 mb-3 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Securitate
              </h4>
              {!showChangePassword ? (
                <Button 
                  variant="outline" 
                  className="w-full border-slate-200 hover:bg-white/30"
                  onClick={() => setShowChangePassword(true)}
                  data-testid="change-password-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Schimbă Parola
                </Button>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-slate-400 text-xs">Parola curentă</Label>
                    <Input 
                      type="password"
                      value={passwordForm.current}
                      onChange={(e) => setPasswordForm({...passwordForm, current: e.target.value})}
                      className="bg-white/40 border-slate-200"
                      placeholder="Introdu parola curentă"
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Parola nouă</Label>
                    <Input 
                      type="password"
                      value={passwordForm.new}
                      onChange={(e) => setPasswordForm({...passwordForm, new: e.target.value})}
                      className="bg-white/40 border-slate-200"
                      placeholder="Minim 6 caractere"
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">Confirmă parola nouă</Label>
                    <Input 
                      type="password"
                      value={passwordForm.confirm}
                      onChange={(e) => setPasswordForm({...passwordForm, confirm: e.target.value})}
                      className="bg-white/40 border-slate-200"
                      placeholder="Repetă parola nouă"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleChangePassword}
                      disabled={passwordLoading}
                      className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                      data-testid="save-password-btn"
                    >
                      {passwordLoading ? "Se salvează..." : "Salvează"}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => { setShowChangePassword(false); setPasswordForm({ current: "", new: "", confirm: "" }); }}
                      className="border-slate-200 hover:bg-white/30"
                    >
                      Anulează
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* About Platform Button */}
            <Button 
              onClick={() => {
                setShowSettingsDialog(false);
                setShowAboutDialog(true);
              }}
              className="w-full bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900"
            >
              <Info className="w-4 h-4 mr-2" />
              DESPRE PLATFORMĂ
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* About Platform Dialog */}
      <Dialog open={showAboutDialog} onOpenChange={setShowAboutDialog}>
        <DialogContent className="bg-white/70 backdrop-blur-xl border-white/50 text-slate-800 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Info className="w-6 h-6 text-yellow-500" />
              Despre Platforma e-Tesa
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Ghid complet al funcționalităților și structurii aplicației
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {/* Dashboard Worker */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button 
                onClick={() => setExpandedSections(prev => ({...prev, worker: !prev.worker}))}
                className="w-full p-4 bg-white/30 flex items-center justify-between hover:bg-white/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold">Dashboard Angajat (Worker)</h3>
                    <p className="text-xs text-slate-400">Funcții disponibile pentru angajați</p>
                  </div>
                </div>
                {expandedSections.worker ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedSections.worker && (
                <div className="p-4 bg-white/30/50 space-y-3 text-sm">
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-teal-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-teal-400" />
                      <span className="font-medium">PREZENT / SOSIT</span>
                    </div>
                    <p className="text-slate-400 text-xs">Check-in la începutul programului de lucru</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Generează notificare pentru Admin + înregistrare în Calendar Pontaj</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-teal-400">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-4 h-4 text-teal-400" />
                      <span className="font-medium">Selectare Șantier</span>
                    </div>
                    <p className="text-slate-400 text-xs">Alege din lista de șantiere configurate de Admin</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Legătură cu tab-ul Șantiere din Admin Dashboard</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-orange-400">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-4 h-4 text-orange-400" />
                      <span className="font-medium">Completează Punct de Lucru</span>
                    </div>
                    <p className="text-slate-400 text-xs">Pentru intervenții în alte locații decât șantierele</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Permite pontaj fără șantier predefinit, adresă manuală</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-blue-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Wrench className="w-4 h-4 text-blue-400" />
                      <span className="font-medium">Selectare Unelte</span>
                    </div>
                    <p className="text-slate-400 text-xs">Marchează uneltele utilizate în ziua respectivă</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Legătură cu tab-ul Unelte din Admin + raport ore utilizare</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-orange-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Car className="w-4 h-4 text-orange-400" />
                      <span className="font-medium">Mașină de Firmă</span>
                    </div>
                    <p className="text-slate-400 text-xs">Activează monitorizarea traseului și foaia de parcurs</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Generează automat Foaie de Parcurs în tab-ul F.Parcurs + Documente</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-blue-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-blue-400" />
                      <span className="font-medium">Solicit Concediu</span>
                    </div>
                    <p className="text-slate-400 text-xs">Cerere oficială CO/CM/CS/FS cu generare automată document</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Apare în Cereri în Așteptare (Admin) + Notificări + Documente la aprobare</p>
                  </div>
                </div>
              )}
            </div>

            {/* Dashboard Admin/Master */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button 
                onClick={() => setExpandedSections(prev => ({...prev, admin: !prev.admin}))}
                className="w-full p-4 bg-white/30 flex items-center justify-between hover:bg-white/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <UserCog className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold">Dashboard Administrator Firmă</h3>
                    <p className="text-xs text-slate-400">Panou de control pentru administratori</p>
                  </div>
                </div>
                {expandedSections.admin ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedSections.admin && (
                <div className="p-4 bg-white/30/50 space-y-2 text-sm">
                  <div className="flex items-center gap-2 p-2 rounded bg-slate-700/50">
                    <Home className="w-4 h-4 text-blue-400" />
                    <span className="font-medium">Acasă</span>
                    <span className="text-slate-400">- Sumar prezențe și statistici</span>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-red-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Bell className="w-4 h-4 text-red-400" />
                      <span className="font-medium">Notificări</span>
                    </div>
                    <p className="text-slate-400 text-xs">Cereri concediu, check-in/out, alerte sistem</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Primește notificări de la: Angajați (check-in, concedii), Sistem (erori)</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-green-400">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-4 h-4 text-green-400" />
                      <span className="font-medium">Șantiere</span>
                    </div>
                    <p className="text-slate-400 text-xs">Creare, editare locații de muncă cu coordonate GPS</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Apar în Selectare Șantier (Worker) + Sarcini + Rapoarte</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-blue-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Wrench className="w-4 h-4 text-blue-400" />
                      <span className="font-medium">Unelte</span>
                    </div>
                    <p className="text-slate-400 text-xs">Inventar echipamente și utilaje firmă</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Apar în Selectare Unelte (Worker) + Ore utilizare în Acasă</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-teal-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-teal-400" />
                      <span className="font-medium">Angajați</span>
                    </div>
                    <p className="text-slate-400 text-xs">Creare conturi, asignare la proiecte</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Pot accesa Worker Dashboard + apar în Calendar Pontaj</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-orange-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-orange-400" />
                      <span className="font-medium">Pontaj</span>
                    </div>
                    <p className="text-slate-400 text-xs">Calendar prezențe, cereri concediu, sărbători legale</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Date de la: Check-in Worker + Cereri concediu + Export PDF/Excel</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-yellow-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Car className="w-4 h-4 text-yellow-400" />
                      <span className="font-medium">Foi de Parcurs</span>
                    </div>
                    <p className="text-slate-400 text-xs">Trasee vehicule firmă, km parcurși</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Generate automat de Worker (Mașină Firmă) + salvate în Documente</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-indigo-400">
                    <div className="flex items-center gap-2 mb-1">
                      <FileCheck className="w-4 h-4 text-indigo-400" />
                      <span className="font-medium">Sarcini</span>
                    </div>
                    <p className="text-slate-400 text-xs">Tichete intervenții cu prioritate și tracking timp</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Asignate la Angajați, apar în Worker Dashboard, status în timp real</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-pink-400">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-pink-400" />
                      <span className="font-medium">Documente</span>
                    </div>
                    <p className="text-slate-400 text-xs">Registru automat cu numerotare</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Populate automat de: Cereri concediu aprobate + Foi de parcurs</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-cyan-400">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-cyan-400" />
                      <span className="font-medium">Deviz</span>
                    </div>
                    <p className="text-slate-400 text-xs">Generator devize estimative cu bibliotecă salvată</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Salvare automată draft + export PDF/print + auto-numerotare</p>
                  </div>
                </div>
              )}
            </div>

            {/* Dashboard Superadmin */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button 
                onClick={() => setExpandedSections(prev => ({...prev, superadmin: !prev.superadmin}))}
                className="w-full p-4 bg-white/30 flex items-center justify-between hover:bg-white/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                    <Crown className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold">Dashboard Super Administrator</h3>
                    <p className="text-xs text-slate-400">Control complet al platformei</p>
                  </div>
                </div>
                {expandedSections.superadmin ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedSections.superadmin && (
                <div className="p-4 bg-white/30/50 space-y-3 text-sm">
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-blue-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="w-4 h-4 text-blue-400" />
                      <span className="font-medium">Firme</span>
                    </div>
                    <p className="text-slate-400 text-xs">Creare, gestionare, ștergere conturi firme</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Fiecare firmă are propriul Admin Dashboard izolat</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-green-400">
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="w-4 h-4 text-green-400" />
                      <span className="font-medium">Plăți</span>
                    </div>
                    <p className="text-slate-400 text-xs">Monitorizare abonamente și facturare</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Status abonament per firmă (în dezvoltare)</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-red-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Headphones className="w-4 h-4 text-red-400" />
                      <span className="font-medium">Suport</span>
                    </div>
                    <p className="text-slate-400 text-xs">Erori raportate de toate firmele</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Primește erori de la AI Chat Widget (RAPORTEAZĂ EROARE)</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-yellow-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Settings className="w-4 h-4 text-yellow-400" />
                      <span className="font-medium">Gestionează Firmă</span>
                    </div>
                    <p className="text-slate-400 text-xs">Accesează dashboard-ul oricărei firme ca admin</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Deschide Admin Dashboard cu datele firmei selectate</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-blue-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Eye className="w-4 h-4 text-blue-400" />
                      <span className="font-medium">Vizualizare Angajați</span>
                    </div>
                    <p className="text-slate-400 text-xs">Lista angajaților per firmă</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Vedere rapidă fără a intra în Admin Dashboard</p>
                  </div>
                </div>
              )}
            </div>

            {/* AI Chat Widget */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button 
                onClick={() => setExpandedSections(prev => ({...prev, chat: !prev.chat}))}
                className="w-full p-4 bg-white/30 flex items-center justify-between hover:bg-white/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold">AI Chat Assistant</h3>
                    <p className="text-xs text-slate-400">Asistent inteligent și raportare erori</p>
                  </div>
                </div>
                {expandedSections.chat ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {expandedSections.chat && (
                <div className="p-4 bg-white/30/50 space-y-3 text-sm">
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-green-400">
                    <div className="flex items-center gap-2 mb-1">
                      <HelpCircle className="w-4 h-4 text-green-400" />
                      <span className="font-medium">Întrebări Construcții</span>
                    </div>
                    <p className="text-slate-400 text-xs">Prețuri materiale, sfaturi tehnice, calcule</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Răspunsuri instant bazate pe AI pentru întrebări despre construcții</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-blue-400">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-blue-400" />
                      <span className="font-medium">RAPORTEAZĂ EROARE</span>
                    </div>
                    <p className="text-slate-400 text-xs">Trimite bug-uri către echipa de suport</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ Creează automat un tichet în tab-ul Suport al SuperAdmin</p>
                  </div>
                  <div className="p-3 rounded bg-slate-700/50 border-l-2 border-red-400">
                    <div className="flex items-center gap-2 mb-1">
                      <Eye className="w-4 h-4 text-red-400" />
                      <span className="font-medium">STATUS ERORI</span>
                    </div>
                    <p className="text-slate-400 text-xs">Vezi erorile raportate de firma ta</p>
                    <p className="text-slate-500 text-xs mt-1 italic">→ SuperAdmin vede toate erorile, Admin-ii doar pe ale firmei lor</p>
                  </div>
                </div>
              )}
            </div>

            {/* Technical Info */}
            <div className="p-4 bg-gradient-to-r from-slate-800 to-slate-700 rounded-lg border border-slate-200">
              <h4 className="font-semibold text-yellow-400 mb-2">Informații Tehnice</h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="text-slate-400">Versiune:</div>
                <div className="text-slate-800">1.0.0</div>
                <div className="text-slate-400">Backend:</div>
                <div className="text-slate-800">FastAPI + MongoDB</div>
                <div className="text-slate-400">Frontend:</div>
                <div className="text-slate-800">React + TailwindCSS</div>
                <div className="text-slate-400">AI:</div>
                <div className="text-slate-800">GPT-5.2 (Emergent)</div>
              </div>
            </div>

            {/* Generate PDF Button */}
            <Button 
              onClick={generatePlatformPDF}
              className="w-full bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700"
            >
              <Download className="w-4 h-4 mr-2" />
              DESCARCĂ GHID PDF (Cuprins Detaliat)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Company Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl bg-white/70 backdrop-blur-xl border-white/50 text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-xl text-yellow-400 flex items-center gap-2">
              <Download className="w-5 h-5 rotate-180" />
              Importă Firmă din Backup
            </DialogTitle>
          </DialogHeader>
          
          {importData && (
            <div className="space-y-4">
              {/* Preview */}
              <div className="p-4 bg-white/30 rounded-lg border border-slate-700">
                <h4 className="font-semibold text-lg text-green-400 mb-2">
                  {importData.type === 'single' 
                    ? `Firmă: ${importData.data.company_name}` 
                    : `${importData.companies.length} firme în backup`
                  }
                </h4>
                {importData.type === 'single' && (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-slate-400">Email:</span>
                    <span>{importData.data.company_email}</span>
                    <span className="text-slate-400">Total înregistrări:</span>
                    <span className="text-yellow-400 font-mono">{importData.data.total_records}</span>
                    <span className="text-slate-400">Exportat la:</span>
                    <span>{new Date(importData.data.exported_at).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    {importData.data.stats && Object.entries(importData.data.stats).map(([key, val]) => (
                      <Fragment key={key}>
                        <span className="text-slate-400 capitalize">{key}:</span>
                        <span>{val}</span>
                      </Fragment>
                    ))}
                  </div>
                )}
                {importData.type === 'multi' && (
                  <div className="space-y-2">
                    {importData.companies.map((c, i) => (
                      <div key={c.company_name || i} className="flex items-center justify-between p-2 bg-slate-700 rounded">
                        <span>{c.company_name}</span>
                        <span className="text-yellow-400 text-sm">{c.total_records} înregistrări</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Import Mode Selection */}
              <div className="space-y-3">
                <h4 className="font-semibold text-slate-800">Mod de import:</h4>
                <div className="grid grid-cols-1 gap-3">
                  <label className={`p-4 rounded-lg border cursor-pointer transition-all ${importMode === 'fresh' ? 'border-green-500 bg-green-500/10' : 'border-slate-200 hover:border-slate-500'}`}>
                    <div className="flex items-start gap-3">
                      <input type="radio" name="mode" value="fresh" checked={importMode === 'fresh'} onChange={() => setImportMode('fresh')} className="mt-1" />
                      <div>
                        <p className="font-semibold text-green-400">Pornire de la 0 (Firmă nouă)</p>
                        <p className="text-sm text-slate-400">Creează o firmă nouă cu toate datele din backup. Firma originală (dacă există) rămâne neschimbată.</p>
                      </div>
                    </div>
                  </label>
                  <label className={`p-4 rounded-lg border cursor-pointer transition-all ${importMode === 'upgrade' ? 'border-blue-500 bg-blue-500/10' : 'border-slate-200 hover:border-slate-500'}`}>
                    <div className="flex items-start gap-3">
                      <input type="radio" name="mode" value="upgrade" checked={importMode === 'upgrade'} onChange={() => setImportMode('upgrade')} className="mt-1" />
                      <div>
                        <p className="font-semibold text-blue-400">Actualizare (Upgrade)</p>
                        <p className="text-sm text-slate-400">Suprascrie/actualizează datele firmei existente. Datele noi sunt adăugate, cele existente sunt actualizate.</p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                {importData.type === 'single' ? (
                  <Button
                    onClick={() => handleImportCompany(importData.data)}
                    disabled={importLoading}
                    className="flex-1 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
                  >
                    {importLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2 rotate-180" />}
                    {importLoading ? 'Se importă...' : 'Importă Firma'}
                  </Button>
                ) : (
                  importData.companies.map((c) => (
                    <Button
                      key={c.company_name}
                      onClick={() => handleImportCompany(c)}
                      disabled={importLoading}
                      className="flex-1 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-xs"
                    >
                      {importLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                      Importă {c.company_name}
                    </Button>
                  ))
                )}
                <Button variant="outline" onClick={() => { setShowImportDialog(false); setImportData(null); }} className="border-slate-200 text-slate-500">
                  Anulează
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Backups Management Dialog */}
      <Dialog open={showBackupsDialog} onOpenChange={setShowBackupsDialog}>
        <DialogContent className="max-w-2xl bg-white/70 backdrop-blur-xl border-white/50 text-slate-800">
          <DialogHeader>
            <DialogTitle className="text-xl text-blue-400 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Backup-uri Automate (ultimele 7 zile)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-white/30 rounded-lg">
              <div>
                <p className="text-sm text-slate-400">Backup zilnic automat la <span className="text-yellow-400 font-mono">18:00 UTC</span></p>
                <p className="text-xs text-slate-500">Se păstrează ultimele 7 zile (ca o cameră auto)</p>
              </div>
              <Button
                onClick={handleRunBackupNow}
                disabled={backupRunning}
                className="bg-blue-700 hover:bg-blue-800"
                size="sm"
              >
                {backupRunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Backup Acum
              </Button>
            </div>

            {backups.length === 0 ? (
              <p className="text-center text-slate-500 py-6">Niciun backup disponibil. Rulați primul backup manual.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {backups.map((backup) => (
                  <div key={backup.filename} className="flex items-center justify-between p-3 bg-white/30 rounded-lg border border-slate-700">
                    <div>
                      <p className="font-mono text-sm text-green-400">{backup.filename}</p>
                      <p className="text-xs text-slate-500">{backup.size_mb} MB • {new Date(backup.created_at).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadBackup(backup.filename)}
                      className="border-green-500 text-green-400 hover:bg-green-500/10"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Descarcă
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Allocate Credits Dialog */}
      <Dialog open={showAllocateDialog} onOpenChange={setShowAllocateDialog}>
        <DialogContent className="bg-white/70 backdrop-blur-xl border-white/50 text-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-yellow-500" />
              Atribuire Credite
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {allocateTarget ? `Atribuiți credite pentru ${allocateTarget.company_name}` : ""}
            </DialogDescription>
          </DialogHeader>
          {allocateTarget && (
            <div className="space-y-4">
              <div className="p-3 bg-white/30 rounded-lg border border-slate-700">
                <p className="text-sm text-slate-400">Firma</p>
                <p className="font-semibold text-slate-800">{allocateTarget.company_name}</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span className="text-slate-400">Sold actual: <span className="text-yellow-400 font-bold">{allocateTarget.balance}</span> credite</span>
                  <span className="text-slate-400">Consumate: <span className="text-red-400 font-bold">{allocateTarget.consumed}</span></span>
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Număr credite de atribuit</Label>
                <Input
                  type="number"
                  min="1"
                  max="500"
                  value={allocateAmount}
                  onChange={(e) => setAllocateAmount(e.target.value)}
                  placeholder="Ex: 100"
                  className="bg-white/40 border-slate-200 text-slate-800 mt-1"
                  data-testid="allocate-credits-input"
                />
                <p className="text-xs text-slate-500 mt-1">Maxim 500 credite per firmă</p>
              </div>
              <div>
                <Label className="text-slate-300">Descriere (opțional)</Label>
                <Input
                  value={allocateDesc}
                  onChange={(e) => setAllocateDesc(e.target.value)}
                  className="bg-white/40 border-slate-200 text-slate-800 mt-1"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" className="border-slate-200 text-slate-500" onClick={() => setShowAllocateDialog(false)}>Anulează</Button>
                <Button
                  onClick={handleAllocateCredits}
                  disabled={allocating || !allocateAmount}
                  className="bg-yellow-600 hover:bg-yellow-700"
                  data-testid="confirm-allocate-btn"
                >
                  {allocating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                  Atribuie {allocateAmount || 0} Credite
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transactions History Dialog */}
      <Dialog open={showTransactionsDialog} onOpenChange={setShowTransactionsDialog}>
        <DialogContent className="bg-white/70 backdrop-blur-xl border-white/50 text-slate-800 max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-yellow-500" />
              Istoric Tranzacții - {transactionsTarget?.company_name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Toate tranzacțiile de credite ale firmei
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[500px] pr-3">
            {transactionsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
              </div>
            ) : transactions.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Nu există tranzacții</p>
            ) : (
              <div className="space-y-2">
                {transactions.map((t) => (
                  <div key={t.id || t.created_at} className={`p-3 rounded-lg border ${t.type === 'credit' ? 'bg-green-900/20 border-green-800/30' : 'bg-red-900/20 border-red-800/30'}`}>
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">{t.description || t.action_code}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {t.created_at ? formatDateTimeRO(t.created_at) : "-"}
                          {t.created_by && ` | De: ${t.created_by}`}
                        </p>
                      </div>
                      <span className={`font-bold text-lg ${t.type === 'credit' ? 'text-green-400' : 'text-red-400'}`}>
                        {t.type === 'credit' ? '+' : '-'}{t.amount}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </GlassLayout>
  );
}


// ============ PaymentsTab Component ============
function PaymentsTab({ companyCredits, creditsLoading, creditsSortBy, setCreditsSortBy, fetchCompanyCredits, getSortedCredits, onAllocate, onViewTransactions, actionPrices, pricesLoading, editingPrices, setEditingPrices, fetchActionPrices, handleSavePrice, savingPrice }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showPrices, setShowPrices] = useState(false);

  // Credit Requests state
  const [creditRequests, setCreditRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestsFilter, setRequestsFilter] = useState("pending"); // pending | approved | all

  // Facturi & Proforme state
  const [invoicesDocs, setInvoicesDocs] = useState([]);
  const [invoicesFilter, setInvoicesFilter] = useState("all");
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [previewInvoiceDoc, setPreviewInvoiceDoc] = useState(null);

  const fetchAllInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      const params = invoicesFilter !== "all" ? { status: invoicesFilter } : {};
      const r = await axios.get(`${API}/superadmin/invoices-and-proformas`, { params });
      setInvoicesDocs(r.data || []);
    } catch (e) {
      console.error("Eroare facturi:", e);
    } finally {
      setLoadingInvoices(false);
    }
  }, [invoicesFilter]);

  const fetchCreditRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const params = requestsFilter !== "all" ? { status: requestsFilter } : {};
      const r = await axios.get(`${API}/superadmin/credit-requests`, { params });
      setCreditRequests(r.data || []);
    } catch (e) {
      console.error("Eroare cereri credite:", e);
    } finally {
      setLoadingRequests(false);
    }
  }, [requestsFilter]);

  useEffect(() => {
    fetchCompanyCredits();
    fetchActionPrices();
    fetchCreditRequests();
    fetchAllInvoices();
  }, [fetchCompanyCredits, fetchActionPrices, fetchCreditRequests, fetchAllInvoices]);

  const handleApproveRequest = async (id) => {
    if (!window.confirm("Aprobi cererea? Se va emite o factură de la firma proprietară (CUI 44303561) care apare în Facturi Primite ale clientului.")) return;
    try {
      const r = await axios.post(`${API}/superadmin/credit-requests/${id}/approve`);
      toast.success(r.data?.message || "Cerere aprobată — factură emisă");
      fetchCreditRequests();
      fetchCompanyCredits();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare aprobare");
    }
  };

  const handleMarkPaid = async (id) => {
    if (!window.confirm("Confirmi plata? Se alocă creditele rămase clientului și factura devine PLĂTITĂ.")) return;
    try {
      const r = await axios.post(`${API}/superadmin/credit-requests/${id}/mark-paid`);
      toast.success(r.data?.message || "Plată confirmată");
      fetchCreditRequests();
      fetchCompanyCredits();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare confirmare plată");
    }
  };

  const handleRejectRequest = async (id) => {
    const reason = window.prompt("Motiv respingere (opțional):") || "";
    try {
      await axios.post(`${API}/superadmin/credit-requests/${id}/reject`, { reason });
      toast.success("Cerere respinsă");
      fetchCreditRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eroare respingere");
    }
  };

  const statusBadge = (s) => {
    const cfg = {
      pending:  { color: "bg-amber-100 text-amber-800 border-amber-300", label: "În așteptare" },
      approved: { color: "bg-blue-100 text-blue-800 border-blue-300", label: "Aprobată — factură emisă" },
      paid:     { color: "bg-green-100 text-green-800 border-green-300", label: "Plătită" },
      rejected: { color: "bg-red-100 text-red-800 border-red-300", label: "Respinsă" },
    }[s] || { color: "bg-slate-100 text-slate-700", label: s };
    return <span className={`px-2 py-1 rounded-full text-[10px] font-bold border ${cfg.color}`}>{cfg.label}</span>;
  };

  const totalConsumed = companyCredits.reduce((sum, c) => sum + (c.consumed || 0), 0);
  const totalBalance = companyCredits.reduce((sum, c) => sum + (c.balance || 0), 0);
  const totalAdded = companyCredits.reduce((sum, c) => sum + (c.total_added || 0), 0);
  const lowCreditCompanies = companyCredits.filter(c => c.low_credits);

  const sortOptions = [
    { value: "consumed_desc", label: "Credite consumate (desc.)" },
    { value: "consumed_asc", label: "Credite consumate (asc.)" },
    { value: "balance_desc", label: "Sold curent (desc.)" },
    { value: "balance_asc", label: "Sold curent (asc.)" },
    { value: "name_asc", label: "Nume firmă (A-Z)" },
    { value: "name_desc", label: "Nume firmă (Z-A)" },
    { value: "date_desc", label: "Data înscrierii (recent)" },
    { value: "date_asc", label: "Data înscrierii (vechi)" },
  ];

  const sorted = getSortedCredits();
  const filteredSorted = searchQuery.trim()
    ? sorted.filter(c => c.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) || c.cui?.toLowerCase().includes(searchQuery.toLowerCase()))
    : sorted;

  return (
    <div className="space-y-6" data-testid="payments-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-yellow-600/20 to-yellow-800/10 border-yellow-700/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-yellow-400">{companyCredits.length}</p>
            <p className="text-sm text-yellow-300/70">Firme Înregistrate</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-600/20 to-red-800/10 border-red-700/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-400">{totalConsumed.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-sm text-red-300/70">Total Consumat</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-600/20 to-green-800/10 border-green-700/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{totalBalance.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-sm text-green-300/70">Sold Total Disponibil</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-600/20 to-blue-800/10 border-blue-700/30">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-400">{totalAdded.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-sm text-blue-300/70">Total Atribuit</p>
          </CardContent>
        </Card>
      </div>

      {/* Low Credits Alert */}
      {lowCreditCompanies.length > 0 && (
        <Card className="bg-gradient-to-r from-red-900/40 to-orange-900/30 border-red-600/50 animate-pulse-slow" data-testid="low-credits-alert">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-600/30 flex items-center justify-center flex-shrink-0">
                <Bell className="w-5 h-5 text-red-400" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-red-300 flex items-center gap-2">
                  ALARMĂ: Credite scăzute ({lowCreditCompanies.length} {lowCreditCompanies.length === 1 ? 'firmă' : 'firme'})
                </h4>
                <p className="text-sm text-red-200/70 mt-1">
                  Următoarele firme au sub 50 de credite și necesită atenție:
                </p>
                <div className="mt-2 space-y-1">
                  {lowCreditCompanies.map(c => (
                    <div key={c.company_id} className="flex items-center justify-between bg-red-950/40 px-3 py-2 rounded-lg border border-red-800/30">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="font-medium text-slate-800 text-sm">{c.company_name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-red-400 font-bold text-sm">{c.balance} credite</span>
                        <Button
                          size="sm"
                          className="bg-yellow-600 hover:bg-yellow-700 text-white h-7 text-xs"
                          onClick={() => onAllocate(c)}
                        >
                          <Plus className="w-3 h-3 mr-1" /> Atribuie
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Credit Requests Section */}
      <Card className="bg-white/45 backdrop-blur-xl border-white/60" data-testid="credit-requests-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-slate-800 flex items-center gap-2">
              <Bell className="w-5 h-5 text-emerald-500" />
              Cereri Credite de la Clienți
              {creditRequests.filter(r => r.status === "pending").length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-2 rounded-full text-[11px] font-bold bg-red-500 text-white animate-pulse">
                  {creditRequests.filter(r => r.status === "pending").length} noi
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <select
                value={requestsFilter}
                onChange={(e) => setRequestsFilter(e.target.value)}
                className="text-xs sm:text-sm border rounded-md px-2 py-1 bg-white"
                data-testid="credit-requests-filter"
              >
                <option value="pending">În așteptare</option>
                <option value="approved">Aprobate (de plată)</option>
                <option value="paid">Plătite</option>
                <option value="rejected">Respinse</option>
                <option value="all">Toate</option>
              </select>
              <Button size="sm" variant="outline" onClick={fetchCreditRequests} data-testid="refresh-credit-requests">
                <RefreshCw className="w-3 h-3 mr-1" /> Actualizează
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingRequests ? (
            <div className="text-center py-6 text-slate-500">Se încarcă...</div>
          ) : creditRequests.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Bell className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>Nu există cereri în această categorie</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[450px]">
              <div className="space-y-2 pr-2">
                {creditRequests.map((r) => (
                  <div key={r.id} className="p-3 border rounded-lg bg-white hover:bg-slate-50 transition" data-testid={`sa-request-${r.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Building2 className="w-4 h-4 text-slate-500" />
                          <p className="font-bold text-slate-800 text-sm sm:text-base">{r.company_name}</p>
                          <span className="text-[10px] sm:text-xs text-slate-400">CUI: {r.company_cui || "—"}</span>
                        </div>
                        <p className="text-xl sm:text-2xl font-bold text-emerald-600">
                          {Number(r.amount_requested || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-xs font-normal text-slate-400">credite</span>
                        </p>
                        <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5">
                          Trimisă: {r.created_at ? formatDateTimeRO(r.created_at) : "-"}
                        </p>
                        {r.advance_granted > 0 && (
                          <p className="text-[10px] sm:text-xs text-emerald-700 mt-1">
                            ✓ Avans {r.advance_granted} credite acordat instant la cerere
                          </p>
                        )}
                        {r.notes && (
                          <p className="text-[11px] sm:text-xs text-slate-600 italic mt-1">„{r.notes}"</p>
                        )}
                        {r.reject_reason && (
                          <p className="text-[11px] sm:text-xs text-red-600 mt-1">Motiv respingere: {r.reject_reason}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        {statusBadge(r.status)}
                        <div className="flex gap-1 flex-wrap justify-end">
                          {r.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs"
                                onClick={() => handleApproveRequest(r.id)}
                                data-testid={`sa-approve-${r.id}`}
                                title="Cerere veche pending — emite proforma acum"
                              >
                                ✓ Procesează (emite proformă)
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-700 h-8 text-xs"
                                onClick={() => handleRejectRequest(r.id)}
                                data-testid={`sa-reject-${r.id}`}
                              >
                                ✗ Respinge
                              </Button>
                            </>
                          )}
                          {r.status === "approved" && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs"
                              onClick={() => handleMarkPaid(r.id)}
                              data-testid={`sa-paid-${r.id}`}
                              title="Confirmă plata proformei — se emite factura fiscală ACHITATĂ"
                            >
                              💰 Confirmă plata (emit factură fiscală)
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Facturi & Proforme Section */}
      <Card className="bg-white/45 backdrop-blur-xl border-white/60" data-testid="invoices-proformas-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-slate-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Facturi & Proforme Emise de e-Tesa
              <span className="text-xs font-normal text-slate-500">(total: {invoicesDocs.length})</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <select
                value={invoicesFilter}
                onChange={(e) => setInvoicesFilter(e.target.value)}
                className="text-xs sm:text-sm border rounded-md px-2 py-1 bg-white"
                data-testid="invoices-filter"
              >
                <option value="all">Toate</option>
                <option value="proforma">Doar Proforme</option>
                <option value="fiscala">Doar Facturi Fiscale</option>
                <option value="pending">Proforme De Achitat</option>
                <option value="paid">Achitate</option>
              </select>
              <Button size="sm" variant="outline" onClick={fetchAllInvoices} data-testid="refresh-invoices">
                <RefreshCw className="w-3 h-3 mr-1" /> Actualizează
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loadingInvoices ? (
            <div className="text-center py-6 text-slate-500">Se încarcă...</div>
          ) : invoicesDocs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>Nu există documente</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[400px]">
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-2 text-left">Document</th>
                      <th className="p-2 text-left">Tip</th>
                      <th className="p-2 text-left">Client</th>
                      <th className="p-2 text-right">Total</th>
                      <th className="p-2 text-center">Status</th>
                      <th className="p-2 text-left">Data</th>
                      <th className="p-2 text-center">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoicesDocs.map((d) => {
                      const isProforma = d.invoice_doc_type === 'proforma';
                      const isAchitata = d.payment_status === 'achitata';
                      const docNo = `${d.series}${String(d.number).padStart(4,'0')}`;
                      return (
                        <tr key={d.id} className="border-b hover:bg-slate-50" data-testid={`sa-doc-${d.id}`}>
                          <td className="p-2 font-bold">{docNo}</td>
                          <td className="p-2">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isProforma ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                              {isProforma ? 'PROFORMĂ' : 'FISCALĂ'}
                            </span>
                          </td>
                          <td className="p-2 truncate max-w-[180px]" title={d.client_name}>{d.client_name}</td>
                          <td className="p-2 text-right font-semibold">{Number(d.total || 0).toFixed(2)} RON</td>
                          <td className="p-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isAchitata ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                              {isAchitata ? '✓ ACHITATĂ' : '⏰ DE ACHITAT'}
                            </span>
                          </td>
                          <td className="p-2 text-slate-500">{d.invoice_date}</td>
                          <td className="p-2 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setPreviewInvoiceDoc(d)}
                              data-testid={`view-invoice-${d.id}`}
                              className="text-xs h-7"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              Vizualizează {isProforma ? 'proforma' : 'factura'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Invoice / Proforma Preview Dialog (SuperAdmin) */}
      <InvoicePreviewDialog
        doc={previewInvoiceDoc}
        onClose={() => setPreviewInvoiceDoc(null)}
      />


      {/* Action Prices Table - Editabil */}
      <Card className="bg-white/45 backdrop-blur-xl border-white/60" data-testid="action-prices-card">
        <CardHeader className="pb-3 cursor-pointer" onClick={() => setShowPrices(!showPrices)}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-800 flex items-center gap-2">
              <Settings className="w-5 h-5 text-orange-400" />
              Preturi Actiuni (Credite)
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-slate-500 text-slate-300 text-xs">
                {actionPrices.length} actiuni
              </Badge>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showPrices ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </CardHeader>
        {showPrices && (
          <CardContent>
            {pricesLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-orange-400" />
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_100px_80px] gap-3 px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide border-b border-slate-700">
                  <span>Actiune</span>
                  <span className="text-center">Pret (credite)</span>
                  <span className="text-center">Salveaza</span>
                </div>
                <ScrollArea className="h-[360px]">
                  <div className="space-y-1 pr-2">
                    {actionPrices.map((action) => {
                      const isEdited = editingPrices[action.action_code] !== undefined && 
                        parseFloat(editingPrices[action.action_code]) !== action.price;
                      const isSaving = savingPrice === action.action_code;
                      return (
                        <div 
                          key={action.action_code} 
                          className={`grid grid-cols-[1fr_100px_80px] gap-3 items-center px-3 py-2.5 rounded-lg transition-colors ${isEdited ? 'bg-orange-900/20 border border-orange-600/30' : 'bg-white/30/50 hover:bg-white/30'}`}
                          data-testid={`price-row-${action.action_code}`}
                        >
                          <div>
                            <p className="text-sm font-medium text-slate-800">{action.name}</p>
                            <p className="text-xs text-slate-500">{action.action_code}</p>
                          </div>
                          <div className="flex justify-center">
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              value={editingPrices[action.action_code] ?? action.price}
                              onChange={(e) => setEditingPrices(prev => ({
                                ...prev,
                                [action.action_code]: e.target.value
                              }))}
                              className="w-20 bg-white/40 border border-slate-200 text-slate-800 text-sm text-center rounded-md px-2 py-1 focus:outline-none focus:border-orange-500 transition-colors"
                              data-testid={`price-input-${action.action_code}`}
                            />
                          </div>
                          <div className="flex justify-center">
                            <Button
                              size="sm"
                              variant={isEdited ? "default" : "ghost"}
                              disabled={!isEdited || isSaving}
                              onClick={() => handleSavePrice(action.action_code)}
                              className={isEdited ? "bg-orange-600 hover:bg-orange-700 text-white h-7 text-xs" : "text-slate-500 h-7 text-xs"}
                              data-testid={`price-save-${action.action_code}`}
                            >
                              {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Sort + Search + Actions */}
      <Card className="bg-white/45 backdrop-blur-xl border-white/60">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-slate-800 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-yellow-500" />
              Credite per Firmă
              {searchQuery && <Badge variant="outline" className="border-slate-500 text-slate-300 text-xs ml-2">{filteredSorted.length} rezultate</Badge>}
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Caută firmă sau CUI..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-white/30 border border-slate-200 text-slate-800 text-sm rounded-md pl-8 pr-3 py-1.5 w-[200px] placeholder:text-slate-500 focus:outline-none focus:border-yellow-500 transition-colors"
                  data-testid="credits-search-input"
                />
              </div>
              <select
                value={creditsSortBy}
                onChange={(e) => setCreditsSortBy(e.target.value)}
                className="bg-white/30 border border-slate-200 text-slate-800 text-sm rounded-md px-3 py-1.5"
                data-testid="credits-sort-select"
              >
                {sortOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <Button
                variant="outline"
                size="sm"
                className="border-slate-200 text-slate-500"
                onClick={fetchCompanyCredits}
              >
                <RefreshCw className="w-4 h-4 mr-1" /> Reîncarcă
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {creditsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
            </div>
          ) : filteredSorted.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-400">{searchQuery ? "Nicio firmă găsită" : "Nu sunt firme înregistrate"}</p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="space-y-3 pr-3">
                {filteredSorted.map((company) => {
                  const consumedPct = company.total_added > 0 ? Math.min(100, (company.consumed / company.total_added) * 100) : 0;
                  return (
                    <div
                      key={company.company_id}
                      className={`p-4 rounded-lg border transition-colors ${
                        company.low_credits 
                          ? 'bg-red-900/20 border-red-700/50 hover:border-red-600' 
                          : 'bg-white/30/50 border-slate-700 hover:border-slate-200'
                      }`}
                      data-testid={`credit-row-${company.company_id}`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        {/* Company info */}
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                            <span className="font-semibold text-slate-800">{company.company_name}</span>
                            {company.cui && <span className="text-xs text-slate-500">CUI: {company.cui}</span>}
                            {company.low_credits && (
                              <Badge className="bg-red-600 text-white text-xs animate-pulse ml-1">Sub 50 credite</Badge>
                            )}
                          </div>
                          <div className="flex gap-4 mt-1 text-xs text-slate-400">
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" /> {company.employee_count} angajați
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> {company.created_at ? formatDateRO(company.created_at) : "N/A"}
                            </span>
                          </div>
                        </div>

                        {/* Credits info */}
                        <div className="flex items-center gap-6">
                          <div className="text-center min-w-[80px]">
                            <p className="text-lg font-bold text-red-400">{(company.consumed || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            <p className="text-xs text-slate-500">Consumate</p>
                          </div>
                          <div className="text-center min-w-[80px]">
                            <p className="text-lg font-bold text-yellow-400">{(company.balance || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            <p className="text-xs text-slate-500">Sold</p>
                          </div>
                          <div className="text-center min-w-[80px]">
                            <p className="text-lg font-bold text-green-400">{(company.total_added || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                            <p className="text-xs text-slate-500">Atribuite</p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-slate-200 text-slate-500 hover:text-slate-700"
                            onClick={() => onViewTransactions(company)}
                            title="Istoric tranzacții"
                            data-testid={`view-transactions-${company.company_id}`}
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            className="bg-yellow-600 hover:bg-yellow-700 text-white"
                            onClick={() => onAllocate(company)}
                            data-testid={`allocate-credits-${company.company_id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" /> Atribuie
                          </Button>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="mt-3">
                        <div className="w-full bg-slate-700 rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full bg-gradient-to-r from-yellow-500 to-red-500 transition-all"
                            style={{ width: `${consumedPct}%` }}
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {consumedPct.toFixed(0)}% consumat din totalul atribuit
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
